/**
 * Enhanced presentation (docs/spec/04 §5): 1-2 pixel particles in the cave's own colours, living
 * in cave pixel space so they scroll with the view. Purely visual; seeded, never touching the sim.
 */

import type { SimEvent } from '../../sim/types';
import type { Screen } from '../screen';
import { css } from '../font';
import { CELL } from '../sprites/types';

interface Particle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  life: number;
  max: number;
  colour: number;
  size: number;
  gravity: number;
}

const MAX = 600;

export class Particles {
  private list: Particle[] = [];
  private seed = 1;
  /** Remaining screen-shake time, ms, and its strength in pixels. */
  shakeMs = 0;
  shakePower = 0;

  private rand(): number {
    this.seed = (Math.imul(this.seed, 1664525) + 1013904223) >>> 0;
    return this.seed / 4294967296;
  }

  clear(): void {
    this.list = [];
    this.shakeMs = 0;
  }

  private burst(cx: number, cy: number, n: number, colour: number, speed: number, life: number, gravity: number, size = 1): void {
    for (let i = 0; i < n && this.list.length < MAX; i++) {
      const a = this.rand() * Math.PI * 2;
      const v = speed * (0.35 + this.rand() * 0.65);
      const max = life * (0.6 + this.rand() * 0.6);
      this.list.push({ x: cx, y: cy, vx: Math.cos(a) * v, vy: Math.sin(a) * v - speed * 0.3, life: max, max, colour, size, gravity });
    }
  }

  /** React to one scan's events. `colours` are the cave's c1, c2, c3. */
  events(events: readonly SimEvent[], colours: readonly [number, number, number], shake: boolean): void {
    const [earth, rock, bright] = colours;
    for (const e of events) {
      if (!('x' in e)) continue;
      const cx = e.x * CELL + CELL / 2;
      const cy = e.y * CELL + CELL / 2;
      switch (e.type) {
        case 'dig': this.burst(cx, cy, 5, earth, 0.05, 380, 0.00035); break;
        case 'land':
          this.burst(cx, cy + CELL / 2 - 1, e.what === 'boulder' ? 6 : 3, e.what === 'boulder' ? rock : bright, 0.045, 320, 0.0003);
          break;
        case 'collect': this.burst(cx, cy, 10, bright, 0.07, 520, -0.00005); break;
        case 'push': this.burst(cx, cy + CELL / 2 - 1, 4, rock, 0.04, 300, 0.0003); break;
        case 'explode':
          this.burst(cx, cy, 26, bright, 0.11, 700, 0.00018, 2);
          this.burst(cx, cy, 18, e.kind === 'diamonds' ? bright : earth, 0.08, 900, 0.00025);
          if (shake) {
            this.shakeMs = 260;
            this.shakePower = 2;
          }
          break;
        case 'magicConvert': this.burst(cx, cy + CELL / 2, 6, bright, 0.05, 420, 0.0002); break;
        case 'slime': this.burst(cx, cy, 3, earth, 0.02, 400, 0.0004); break;
        case 'expand': this.burst(cx, cy, 2, rock, 0.03, 250, 0.0002); break;
        default:
      }
    }
  }

  update(dtMs: number): void {
    this.shakeMs = Math.max(0, this.shakeMs - dtMs);
    const keep: Particle[] = [];
    for (const p of this.list) {
      p.life -= dtMs;
      if (p.life <= 0) continue;
      p.vy += p.gravity * dtMs;
      p.x += p.vx * dtMs;
      p.y += p.vy * dtMs;
      keep.push(p);
    }
    this.list = keep;
  }

  /** The current shake offset in whole pixels. */
  shake(): { x: number; y: number } {
    if (this.shakeMs <= 0) return { x: 0, y: 0 };
    const p = Math.ceil(this.shakePower * (this.shakeMs / 260));
    return { x: Math.round((this.rand() * 2 - 1) * p), y: Math.round((this.rand() * 2 - 1) * p) };
  }

  draw(screen: Screen, camX: number, camY: number, top: number, viewW: number, viewH: number): void {
    const ctx = screen.ctx;
    for (const p of this.list) {
      const sx = Math.round(p.x - camX);
      const sy = Math.round(p.y - camY) + top;
      if (sx < 0 || sy < top || sx >= viewW || sy >= top + viewH) continue;
      // Fading particles thin out rather than go translucent: the palette stays the C64's.
      if (p.life < p.max * 0.3 && (Math.floor(p.life / 40) & 1) === 1) continue;
      ctx.fillStyle = css(p.colour);
      ctx.fillRect(sx, sy, p.size * 2, p.size);
    }
  }
}
